import { ContentLoader, OverlayTrigger, Tooltip } from "@rio-cloud/rio-uikit";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchOccurrencesByChassis } from "../../../api/tas";
import {
    getSupportStatusLabel,
    getSupportSubjectLabel,
    getSupportSubtopicIntlLabel,
} from "../getSupportIntlLabels";
import { IOccurrence, OccurrenceStatus } from "../types";

const PREVIEW_SIZE = 5;

const parseDnValue = (dn?: string | null): { code: string; name: string } | null => {
    if (!dn?.trim()) {
        return null;
    }

    const separatorIndex = dn.indexOf("|");
    if (separatorIndex === -1) {
        return { code: dn.trim(), name: "" };
    }

    return {
        code: dn.slice(0, separatorIndex).trim(),
        name: dn.slice(separatorIndex + 1).trim(),
    };
};

const DnCell = ({ dn }: { dn?: string | null }) => {
    const parsed = parseDnValue(dn);

    if (!parsed) {
        return <span>-</span>;
    }

    return (
        <>
            {parsed.code || "-"}
            {parsed.name ? (
                <>
                    <br />
                    {parsed.name}
                </>
            ) : null}
        </>
    );
};

const statusBadgeConfig: Record<OccurrenceStatus, { bg: string; color: string }> = {
    OPEN: { bg: "#F9FAFB", color: "#475467" },
    IN_ANALYSIS: { bg: "#FFFAEB", color: "#B54708" },
    ANSWERED: { bg: "#F2F4F7", color: "#344054" },
    FINISHED: { bg: "#ECFDF3", color: "#027A48" },
};

interface Props {
    chassis?: string;
    currentOccurrenceId?: string | null;
}

const StatusBadge = ({
    status,
    label,
}: {
    status?: OccurrenceStatus;
    label: string;
}) => {
    if (!status) {
        return <span>-</span>;
    }

    const config = statusBadgeConfig[status] ?? statusBadgeConfig.OPEN;

    return (
        <span
            className="text-size-12 text-medium whitespace-nowrap"
            style={{
                background: config.bg,
                color: config.color,
                borderRadius: 8,
                padding: "8px",
                display: "inline-block",
            }}
        >
            {label}
        </span>
    );
};

const HistoryOccurrence = ({ chassis, currentOccurrenceId }: Props) => {
    const intl = useIntl();
    const navigate = useNavigate();
    const location = useLocation();
    const [occurrences, setOccurrences] = useState<IOccurrence[]>([]);
    const [totalElements, setTotalElements] = useState(0);
    const [numberOfElements, setNumberOfElements] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingAll, setIsLoadingAll] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);

    const filterOccurrences = (content: IOccurrence[]) =>
        content.filter((occurrence) => String(occurrence.id) !== String(currentOccurrenceId));

    useEffect(() => {
        const loadOccurrences = async () => {
            if (!chassis || chassis.length !== 17) {
                setOccurrences([]);
                setTotalElements(0);
                setIsExpanded(false);
                return;
            }

            setIsLoading(true);
            setIsExpanded(false);

            try {
                const response = await fetchOccurrencesByChassis(chassis, {
                    page: 0,
                    size: PREVIEW_SIZE,
                });

                setOccurrences(filterOccurrences(response.content));
                setTotalElements(response.totalElements);
                setNumberOfElements(response.numberOfElements);
            } catch {
                setOccurrences([]);
                setTotalElements(0);
            } finally {
                setIsLoading(false);
            }
        };

        loadOccurrences();
    }, [chassis, currentOccurrenceId]); 

    const handleOccurrenceClick = (occurrenceId: number) => {
        const params = new URLSearchParams(location.search);
        params.set("id", String(occurrenceId));
        params.delete("tab");

        navigate(`${location.pathname}?${params.toString()}`);
    };

    const handleViewAll = async () => {
        if (!chassis || isExpanded || isLoadingAll) {
            return;
        }

        setIsLoadingAll(true);

        try {
            const fetchSize = Math.max(totalElements, PREVIEW_SIZE);
            const response = await fetchOccurrencesByChassis(chassis, {
                page: 0,
                size: fetchSize,
            });

            setOccurrences(filterOccurrences(response.content));
            setTotalElements(response.totalElements);
            setIsExpanded(true);
        } catch {
            setIsExpanded(false);
        } finally {
            setIsLoadingAll(false);
        }
    };

    const handleViewLess = async () => {
        if (!chassis || !isExpanded || isLoadingAll) {
            return;
        }

        setIsLoadingAll(true);

        try {
            const response = await fetchOccurrencesByChassis(chassis, {
                page: 0,
                size: PREVIEW_SIZE,
            });

            setOccurrences(filterOccurrences(response.content));
            setTotalElements(response.totalElements);
            setIsExpanded(false);
        } finally {
            setIsLoadingAll(false);
        }
    };

    const handleToggleView = () => {
        if (isExpanded) {
            handleViewLess();
            return;
        }

        handleViewAll();
    };

    const canToggleView = totalElements > PREVIEW_SIZE;

    const renderTableHead = () => (
        <thead>
            <tr>
                <th className="text-size-12 text-color-gray text-normal">
                    {intl.formatMessage({ id: "intl-msg:tas.support.history.column.supportNumber" })}
                </th>
                <th className="text-size-12 text-color-gray text-normal">
                    {intl.formatMessage({ id: "intl-msg:tas.support.history.column.subject" })}
                </th>
                <th className="text-size-12 text-color-gray text-normal">
                    {intl.formatMessage({ id: "intl-msg:tas.support.history.column.theme" })}
                </th>
                <th className="text-size-12 text-color-gray text-normal">
                    {intl.formatMessage({ id: "intl-msg:tas.support.history.column.dn" })}
                </th>
                <th className="text-size-12 text-color-gray text-medium">
                    {intl.formatMessage({ id: "intl-msg:tas.support.history.column.status" })}
                </th>
            </tr>
        </thead>
    );

    const renderTableBody = () => {
        if (isLoading || isLoadingAll) {
            return (
                <tbody>
                    {Array.from({ length: 6 }).map((_, index) => (
                        <tr key={index}>
                            {Array.from({ length: 5 }).map((__, cellIndex) => {
                                const sizes = {
                                    0: 'width-100',
                                    4: 'width-130'
                                };
                                return (
                                    <td key={cellIndex}>
                                        <ContentLoader className={`${sizes[cellIndex as keyof typeof sizes] || 'width-60'} rounded height-30`} />
                                    </td>
                                )
                            })}
                        </tr>
                    ))}
                </tbody>
            );
        }

        if (occurrences.length === 0) {
            return (
                <tbody>
                    <tr>
                        <td colSpan={5} className="text-center text-color-gray text-size-14 padding-y-20">
                            {intl.formatMessage({ id: "intl-msg:tas.support.history.empty" })}
                        </td>
                    </tr>
                </tbody>
            );
        }

        return (
            <tbody>
                {occurrences.map((occurrence) => (
                    <tr key={occurrence.id}>
                        <td>
                            <button
                                type="button"
                                className="btn btn-link padding-0 text-size-12 text-medium text-color-highlight"
                                onClick={() => handleOccurrenceClick(occurrence.id)}
                            >
                                {occurrence.tasOccurrenceNumber || "-"}
                            </button>
                        </td>
                        <td className="text-size-12 text-color-darker">
                            {getSupportSubjectLabel(intl, occurrence.subject)}
                        </td>
                        <td className="text-size-12 text-color-darker">
                            {getSupportSubtopicIntlLabel(intl, occurrence.subtopic)}
                        </td>
                        <td className="text-size-12 text-color-darker">
                            <DnCell dn={occurrence.dn} />
                        </td>
                        <td>
                            <StatusBadge
                                status={occurrence.status}
                                label={getSupportStatusLabel(intl, occurrence.status)}
                            />
                        </td>
                    </tr>
                ))}
            </tbody>
        );
    };

    return (
        <div className="border rounded margin-top-10 overflow-hidden" style={{ width: 540 }}>
            <div className="display-flex justify-content-between align-items-center margin-bottom-15 border border-bottom-only padding-15">
                <span className="text-size-16 text-medium text-color-darker">
                    {intl.formatMessage({ id: "intl-msg:tas.support.history.title" })}
                </span>
                <OverlayTrigger
                    placement="top"
                    overlay={
                        <Tooltip id="history-occurrence-info">
                            {intl.formatMessage({ id: "intl-msg:tas.support.history.tooltip" })}
                        </Tooltip>
                    }
                >
                    <span className="rioglyph rioglyph-info-sign text-color-dark cursor-pointer text-size-16" />
                </OverlayTrigger>
            </div>

            <div className={`table-responsive width-100pct max-height-450 ${isExpanded ? "overflow-y-auto" : ""}`}>
                <table className={"table table-column-overflow-hidden width-100pct margin-bottom-0"}>
                    {renderTableHead()}
                    {renderTableBody()}
                </table>

            </div>

            {!isLoading && canToggleView ? (
                <div className="display-flex justify-content-center padding-y-15">
                    <button
                        type="button"
                        className={`btn btn-link text-size-14 text-medium text-color-highlight ${isLoadingAll ? "btn-loading disabled" : ""}`}
                        onClick={handleToggleView}
                        disabled={isLoadingAll}
                    >
                        {isExpanded
                            ? intl.formatMessage({ id: "intl-msg:tas.support.history.viewLess" })
                            : intl.formatMessage(
                                { id: "intl-msg:tas.support.history.viewMore" },
                                { count: totalElements - numberOfElements },
                            )}
                    </button>
                </div>
            ) : null}
        </div>
    );
};

export default HistoryOccurrence;
